import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone, User, LogOut } from "lucide-react";
import { useState } from "react";
import { useSettings } from "@/lib/settings";
import { useAuth } from "@/lib/auth";

const links = [
  { to: "/", label: "Home" },
  { to: "/products", label: "Products" },
  { to: "/classes", label: "Cooking Classes" },
  { to: "/subscriptions", label: "Subscriptions" },
  { to: "/event-chef", label: "Event Chef" },
  { to: "/blog", label: "Blog" },
  { to: "/faq", label: "FAQ" },
  { to: "/support", label: "Support" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const { settings } = useSettings();
  const { user, logout } = useAuth();
  const isCustomer = user && user.role !== "admin";

  return (
    <header className="glass-header sticky top-0 z-40 w-full" data-testid="site-header">
      <div className="container-x flex items-center justify-between py-4 px-6">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <img src="/logo.jpg" alt="AlikamaFish" className="h-12 w-12 rounded-full object-cover ring-2 ring-[color:var(--color-accent)]/40" />
          <div className="leading-tight">
            <div className="font-display text-2xl text-[color:var(--color-primary)] font-semibold">
              {settings.business_name}
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm tracking-wide ${isActive ? "text-[color:var(--color-accent)] font-medium" : "text-[color:var(--color-primary)]"} hover:text-[color:var(--color-accent)] transition-colors`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {isCustomer ? (
            <>
              <Link to="/account" className="flex items-center gap-2 text-sm text-[color:var(--color-primary)] hover:text-[color:var(--color-accent)]" data-testid="header-account-link">
                <User className="w-4 h-4" /> {user.name?.split(" ")[0] || "Account"}
              </Link>
              <button onClick={logout} className="text-[color:var(--color-primary)] hover:text-[color:var(--color-accent)]" data-testid="header-logout" aria-label="Logout">
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <Link to="/signin" className="text-sm text-[color:var(--color-primary)] hover:text-[color:var(--color-accent)]" data-testid="header-signin-link">
              Sign In
            </Link>
          )}
          <a
            href={`tel:${settings.phone}`}
            className="btn-accent"
            data-testid="header-call-btn"
          >
            <Phone className="w-4 h-4" /> {settings.phone}
          </a>
        </div>

        <button
          className="lg:hidden p-2 text-[color:var(--color-primary)]"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/60 bg-white/95 backdrop-blur-xl" data-testid="mobile-menu">
          <div className="container-x py-4 px-6 flex flex-col gap-3">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `py-2 ${isActive ? "text-[color:var(--color-accent)]" : "text-[color:var(--color-primary)]"} font-medium`
                }
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </NavLink>
            ))}
            {isCustomer ? (
              <Link to="/account" onClick={() => setOpen(false)} className="py-2 text-[color:var(--color-primary)] font-medium">My Account</Link>
            ) : (
              <Link to="/signin" onClick={() => setOpen(false)} className="py-2 text-[color:var(--color-primary)] font-medium">Sign In / Sign Up</Link>
            )}
            <a
              href={`tel:${settings.phone}`}
              className="btn-accent mt-2 justify-center"
              data-testid="mobile-call-btn"
            >
              <Phone className="w-4 h-4" /> Call {settings.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
